import { trim, upperFirst } from 'lodash/fp';
import MiniUser from '../../../../types/MiniUser';
import { sanitize } from '../../../common/services/sanitizer/service';
import PERSONALIZATION_REPLACEMENTS from '../../../react/constants/personalization-replacements';
import { concatAnd } from '../../../react/services/Enumeration';
import * as genderService from '../../../react/services/Gender';

export { isBehindSentenceEnd, personalize, personalizeMulti, personalizeWithHint, personalizeWithHintMulti };

const REPLACEMENT_RE = /{{\s?(.+?)\s?}}/g;

type PersonalizableUser = Pick<MiniUser, 'gender' | 'firstName' | 'lastName' | 'name'>;

interface Generator {
    (value: string): string;
}

function personalize(tpl: string, user: PersonalizableUser, generateReplacement: Generator = defaultGenerator): string {
    tpl = tpl || ''; // can't use default param because it might be null
    const replacements = generateReplacements(user);
    return replaceInTemplate(tpl, replacements, generateReplacement);
}

function personalizeMulti(
    tpl: string,
    users: PersonalizableUser[],
    collectionName: string,
    generateReplacement: Generator = defaultGenerator
): string {
    if (users.length === 1) {
        return personalize(tpl, users[0], generateReplacement);
    }

    tpl = tpl || ''; // can't use default param because it might be null
    const replacements = generateReplacementsMulti(users, collectionName);
    return replaceInTemplate(tpl, replacements, generateReplacement);
}

function replaceInTemplate(tpl: string, replacements: { [key: string]: string }, fallbackGenerator: Generator): string {
    return tpl.replace(REPLACEMENT_RE, function (m, g1, i) {
        const key = trim(g1);
        if (!key) {
            return m;
        }
        let replacement = replacements[key];
        if (!replacement && fallbackGenerator === hintGenerator) {
            replacement = key;
        }
        const val = tryToCapitalize(replacement, tpl, i);
        return fallbackGenerator(val);
    });
}

function personalizeWithHint(tpl: string, user: PersonalizableUser) {
    return personalize(tpl, user, hintGenerator);
}

function personalizeWithHintMulti(tpl: string, users: MiniUser[], collectionName: string) {
    return personalizeMulti(tpl, users, collectionName, hintGenerator);
}

function tryToCapitalize(str: string, context: string, i: number) {
    return i === 0 || isBehindSentenceEnd(context, i) ? upperFirst(str) : str;
}

function isBehindSentenceEnd(context: string, i: number) {
    if (i < 2) {
        return false;
    }
    const before = context[i - 2];
    return /[.?!]/.test(before);
}

function generateReplacements(user: PersonalizableUser) {
    const { gender, firstName, lastName, name } = user;
    return {
        ...genderService.getPronouns(gender),
        [PERSONALIZATION_REPLACEMENTS.FIRST_NAME]: sanitize(firstName),
        [PERSONALIZATION_REPLACEMENTS.LAST_NAME]: sanitize(lastName),
        [PERSONALIZATION_REPLACEMENTS.FULL_NAME]: sanitize(name),
    };
}

function generateReplacementsMulti(users: PersonalizableUser[], collectionName: string) {
    return {
        ...genderService.getPluralPronouns(),
        [PERSONALIZATION_REPLACEMENTS.FIRST_NAME]: enumerateUsers(users, u => u.firstName, 3, collectionName),
        [PERSONALIZATION_REPLACEMENTS.LAST_NAME]: enumerateUsers(users, u => u.lastName || '', 3, collectionName),
        [PERSONALIZATION_REPLACEMENTS.FULL_NAME]: enumerateUsers(users, u => u.name, 3, collectionName),
    };
}

function enumerateUsers(
    users: PersonalizableUser[],
    mapFn: (u: PersonalizableUser) => string,
    maxItems: number,
    fallback: string
) {
    if (users.length > maxItems) {
        return fallback;
    }
    return concatAnd(users.map(mapFn));
}

function defaultGenerator(str: string) {
    return str;
}

function hintGenerator(str: string) {
    return `<autocomplete-blot>${sanitize(str)}</autocomplete-blot>`;
}

export const DUMMY_USER: PersonalizableUser = {
    firstName: 'First Name',
    lastName: 'Last Name',
    name: 'Full Name',
    gender: 'unspecified',
};
